import { useRef, useState, useEffect } from 'react'
import { Box, Button, Typography, Link, CircularProgress } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { shopService } from '../../services'

const MAX_BYTES = 2 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png']
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']

function isAllowedShopImage(file) {
  const type = (file.type || '').toLowerCase()
  if (ALLOWED_IMAGE_TYPES.includes(type)) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  return ALLOWED_IMAGE_EXTENSIONS.includes(ext)
}

function validateShopImage(file) {
  if (file.size > MAX_BYTES) {
    return 'File must be 2MB or smaller'
  }
  if (!isAllowedShopImage(file)) {
    return 'Only JPG, JPEG, and PNG images are allowed'
  }
  return null
}

/**
 * Single shop document upload (trade license, NID, shop photo).
 * Max 2MB, JPG/JPEG/PNG only.
 *
 * With orgId: uploads immediately on file pick.
 * Without orgId: stores file locally via onFileSelected (create wizard step 1).
 */
export default function ShopDocumentUploadField({
  orgId,
  docType,
  label,
  currentUrl,
  pendingFile,
  onUploaded,
  onFileSelected,
  disabled = false,
}) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [url, setUrl] = useState(currentUrl || '')

  useEffect(() => {
    setUrl(currentUrl || '')
  }, [currentUrl])

  const displayUrl = url || currentUrl
  const deferredMode = !orgId

  const handlePick = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setError(null)
    const validationError = validateShopImage(file)
    if (validationError) {
      setError(validationError)
      return
    }

    if (deferredMode) {
      onFileSelected?.(file)
      return
    }

    setUploading(true)
    try {
      const result = await shopService.uploadDocument(orgId, docType, file)
      const next = result?.url || ''
      setUrl(next)
      onUploaded?.(next)
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Upload failed',
      )
    } finally {
      setUploading(false)
    }
  }

  const hasPendingFile = Boolean(pendingFile)
  const showCurrent = displayUrl && !hasPendingFile

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label} (optional)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        JPG, JPEG, or PNG, max 2MB
        {deferredMode ? ' — uploaded when you continue' : ''}
      </Typography>
      {showCurrent ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <Link href={displayUrl} target="_blank" rel="noopener noreferrer">
            View current file
          </Link>
        </Typography>
      ) : hasPendingFile ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Selected: {pendingFile.name}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No file selected
        </Typography>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        hidden
        onChange={handlePick}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={
          uploading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <CloudUploadIcon />
          )
        }
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
      >
        {showCurrent || hasPendingFile ? 'Replace file' : 'Choose file'}
      </Button>
      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

export async function uploadPendingShopDocuments(orgId, pendingDocs) {
  if (!orgId || !pendingDocs) return
  const uploads = Object.entries(pendingDocs).filter(([, file]) => file)
  for (const [docType, file] of uploads) {
    await shopService.uploadDocument(orgId, docType, file)
  }
}
